import { useState, useCallback, useRef, useMemo, useEffect } from 'react'
import ReactFlow, {
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  Background,
} from 'reactflow'
import 'reactflow/dist/style.css'

import Logo from '../components/Logo'
import AtomPalette from '../components/AtomPalette'
import AtomNode from '../components/AtomNode'
import ScoreOverlay from '../components/ScoreOverlay'
import { colorForElement } from '../data/atoms'
import { getHintElements } from '../data/questions'
import { gradeStructure } from '../lib/grading'
import { useAuth } from '@craft/shared'
import { saveScore } from '../lib/firebase'

const nodeTypes = { atom: AtomNode }

// 結合次数ごとの見た目
function bondStyle(order) {
  if (order === 3) return { stroke: '#22D3EE', strokeWidth: 4.5 }
  if (order === 2) return { stroke: '#34D399', strokeWidth: 3.5 }
  return { stroke: '#94A3B8', strokeWidth: 2 }
}
function bondLabel(order) {
  if (order === 3) return '≡'
  if (order === 2) return '＝'
  return ''
}

export default function LearningPage({
  question,
  onBack,
  onNext,
  onPrev,
  currentIndex,
  totalQuestions,
  learningMode = 'guided',
  onLearningModeChange,
}) {
  const { user } = useAuth()
  const [nodes, setNodes] = useState([])
  const [edges, setEdges] = useState([])
  const [bondOrder, setBondOrder] = useState(1)
  const [pendingSourceNodeId, setPendingSourceNodeId] = useState(null)
  const [score, setScore] = useState(null)
  const [showResult, setShowResult] = useState(false)
  const [showingAnswer, setShowingAnswer] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [showMobileInfo, setShowMobileInfo] = useState(false)
  const reactFlowWrapper = useRef(null)
  const [reactFlowInstance, setReactFlowInstance] = useState(null)
  const nodeIdCounter = useRef(0)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const update = () => {
      const coarse = window.matchMedia?.('(pointer: coarse)').matches
      const touch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
      setIsMobile(Boolean(coarse || touch) || window.innerWidth <= 900)
    }
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  const allowedElements = useMemo(
    () => (learningMode === 'guided' ? getHintElements(question) : undefined),
    [learningMode, question]
  )

  const resetCanvas = useCallback(() => {
    setNodes([])
    setEdges([])
    setScore(null)
    setShowResult(false)
    setShowingAnswer(false)
    setPendingSourceNodeId(null)
    nodeIdCounter.current = 0
  }, [])

  // 問題切替で初期化
  useEffect(() => {
    resetCanvas()
  }, [question.id, resetCanvas])

  // ---- React Flow callbacks ----
  const onNodesChange = useCallback(
    (changes) => {
      const removed = changes.filter((c) => c.type === 'remove').map((c) => c.id)
      if (pendingSourceNodeId && removed.includes(pendingSourceNodeId)) {
        setPendingSourceNodeId(null)
      }
      setNodes((nds) => applyNodeChanges(changes, nds))
    },
    [pendingSourceNodeId]
  )
  const onEdgesChange = useCallback(
    (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    []
  )

  const upsertBond = useCallback((sourceId, targetId, order) => {
    if (sourceId === targetId) return
    setEdges((eds) => {
      const existing = eds.find(
        (e) =>
          (e.source === sourceId && e.target === targetId) ||
          (e.source === targetId && e.target === sourceId)
      )
      if (existing) {
        // 既存結合は次数を更新
        return eds.map((e) =>
          e.id === existing.id
            ? {
                ...e,
                data: { order },
                label: bondLabel(order),
                style: bondStyle(order),
              }
            : e
        )
      }
      return addEdge(
        {
          source: sourceId,
          target: targetId,
          data: { order },
          label: bondLabel(order),
          labelStyle: { fill: '#fff', fontWeight: 700, fontSize: 16 },
          labelBgStyle: { fill: 'transparent' },
          style: bondStyle(order),
        },
        eds
      )
    })
  }, [])

  const onConnect = useCallback(
    (connection) => upsertBond(connection.source, connection.target, bondOrder),
    [upsertBond, bondOrder]
  )

  const onNodeClick = useCallback(
    (_event, node) => {
      if (showingAnswer) return
      if (!pendingSourceNodeId) {
        setPendingSourceNodeId(node.id)
        return
      }
      if (pendingSourceNodeId === node.id) {
        setPendingSourceNodeId(null)
        return
      }
      upsertBond(pendingSourceNodeId, node.id, bondOrder)
      setPendingSourceNodeId(null)
    },
    [pendingSourceNodeId, upsertBond, bondOrder, showingAnswer]
  )

  const onPaneClick = useCallback(() => setPendingSourceNodeId(null), [])

  const removeNode = useCallback(
    (nodeId) => {
      setNodes((nds) => nds.filter((n) => n.id !== nodeId))
      setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId))
      if (pendingSourceNodeId === nodeId) setPendingSourceNodeId(null)
    },
    [pendingSourceNodeId]
  )

  const addAtomNode = useCallback(
    (atom, position) => {
      if (showingAnswer) return
      const newId = `${atom.element}-${nodeIdCounter.current++}`
      const base = position || { x: 260, y: 220 }
      setNodes((nds) => {
        const count = nds.length
        const angle = count * 1.35
        const radius = 90 + Math.floor(count / 5) * 60
        const pos = {
          x: Math.round(base.x + Math.cos(angle) * radius),
          y: Math.round(base.y + Math.sin(angle) * radius),
        }
        return [
          ...nds,
          {
            id: newId,
            type: 'atom',
            position: pos,
            data: {
              element: atom.element,
              color: atom.color || colorForElement(atom.element),
              nodeId: newId,
              onRemove: removeNode,
            },
          },
        ]
      })
    },
    [removeNode, showingAnswer]
  )

  const onDragOver = useCallback((e) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }, [])

  const onDrop = useCallback(
    (e) => {
      e.preventDefault()
      if (!reactFlowInstance) return
      const data = e.dataTransfer.getData('application/chemcraft')
      if (!data) return
      const atom = JSON.parse(data)
      const pos = reactFlowInstance.screenToFlowPosition({ x: e.clientX, y: e.clientY })
      addAtomNode(atom, pos)
    },
    [reactFlowInstance, addAtomNode]
  )

  const handleSelectAtom = useCallback(
    (atom) => {
      if (!reactFlowInstance || !reactFlowWrapper.current) {
        addAtomNode(atom, { x: 160, y: 120 })
        return
      }
      const rect = reactFlowWrapper.current.getBoundingClientRect()
      const center = reactFlowInstance.screenToFlowPosition({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      })
      addAtomNode(atom, center)
    },
    [addAtomNode, reactFlowInstance]
  )

  // ---- 正解構造の描画（円形レイアウト） ----
  const showAnswer = useCallback(() => {
    const ansAtoms = question.answer.atoms
    const ansBonds = question.answer.bonds
    const cx = 300
    const cy = 240
    const R = Math.max(120, ansAtoms.length * 26)
    const answerNodes = ansAtoms.map((a, i) => {
      const theta = (i / ansAtoms.length) * Math.PI * 2 - Math.PI / 2
      return {
        id: `ans-${a.id}`,
        type: 'atom',
        position: {
          x: Math.round(cx + Math.cos(theta) * R),
          y: Math.round(cy + Math.sin(theta) * R),
        },
        data: { element: a.element, color: colorForElement(a.element), nodeId: `ans-${a.id}` },
        draggable: true,
        selectable: false,
      }
    })
    const answerEdges = ansBonds.map((b, i) => ({
      id: `ans-edge-${i}`,
      source: `ans-${b.a}`,
      target: `ans-${b.b}`,
      data: { order: b.order },
      label: bondLabel(b.order),
      labelStyle: { fill: '#fff', fontWeight: 700, fontSize: 16 },
      labelBgStyle: { fill: 'transparent' },
      style: bondStyle(b.order),
    }))
    setNodes(answerNodes)
    setEdges(answerEdges)
    setShowingAnswer(true)
    setPendingSourceNodeId(null)
    setTimeout(() => reactFlowInstance?.fitView({ padding: 0.3, duration: 300 }), 60)
  }, [question, reactFlowInstance])

  // ---- 採点 ----
  const checkAnswer = () => {
    const userGraph = {
      atoms: nodes.map((n) => ({ id: n.id, element: n.data.element })),
      bonds: edges.map((e) => ({
        a: e.source,
        b: e.target,
        order: e.data?.order || 1,
      })),
    }
    const result = gradeStructure(userGraph, question.answer)
    setScore(result)
    setShowResult(true)
    if (user) {
      saveScore(user.uid, question.id, result.score, result.isCorrect).catch(console.error)
    }
  }

  const nodesWithRemove = useMemo(
    () =>
      nodes.map((n) => ({
        ...n,
        data: showingAnswer ? { ...n.data, onRemove: undefined } : { ...n.data, onRemove: removeNode },
        // 接続元として選択中のノードを強調
        selected: n.id === pendingSourceNodeId,
      })),
    [nodes, removeNode, pendingSourceNodeId, showingAnswer]
  )

  useEffect(() => {
    if (!reactFlowInstance || nodes.length === 0) return
    reactFlowInstance.fitView({ padding: 0.4, duration: 200, maxZoom: 1.5 })
  }, [reactFlowInstance, nodes.length])

  return (
    <div
      className="flex flex-col overflow-hidden"
      style={{
        height: '100dvh',
        background: 'linear-gradient(180deg, #0a0e1a 0%, #111827 100%)',
        fontFamily: "'Noto Sans JP', 'Inter', sans-serif",
      }}
    >
      {/* Header */}
      <header
        className="flex flex-wrap items-center justify-between gap-2 px-3 sm:px-4 md:px-6 py-2.5 md:py-3 border-b border-white/10 shrink-0"
        style={{ background: 'rgba(10,14,26,0.95)', backdropFilter: 'blur(8px)' }}
      >
        <div className="flex items-center gap-2 md:gap-4 min-w-0">
          <button onClick={onBack} className="text-white/60 hover:text-white text-xs md:text-sm shrink-0">
            ← 問題一覧
          </button>
          <Logo size="sm" />
        </div>
        <div className="flex items-center gap-1.5 md:gap-3 flex-wrap justify-end">
          <div className="flex items-center gap-2">
            <button onClick={onPrev} disabled={currentIndex <= 0}
              className="text-white/40 hover:text-white disabled:opacity-20 text-sm px-2">◀</button>
            <span className="text-xs text-white/50">{currentIndex + 1} / {totalQuestions}</span>
            <button onClick={onNext} disabled={currentIndex >= totalQuestions - 1}
              className="text-white/40 hover:text-white disabled:opacity-20 text-sm px-2">▶</button>
          </div>
          <span className="text-[10px] md:text-xs px-2 md:px-3 py-1 rounded-full font-semibold"
            style={{ background: '#34D39922', color: '#34D399', border: '1px solid #34D39944' }}>
            {question.category}
          </span>
          <span className="text-[10px] md:text-xs px-2 md:px-3 py-1 rounded-full font-semibold"
            style={{ background: '#14B8A622', color: '#5EEAD4', border: '1px solid #14B8A644' }}>
            {question.difficulty}
          </span>
          <div className="flex items-center gap-1 p-1 rounded-lg" style={{ background: 'rgba(255,255,255,0.06)' }}>
            {[
              { key: 'guided', label: 'ヒントあり' },
              { key: 'free', label: 'フリー' },
            ].map((m) => (
              <button key={m.key} onClick={() => onLearningModeChange?.(m.key)}
                className="text-[11px] px-2.5 py-1 rounded-md transition-all"
                style={{
                  background: learningMode === m.key ? '#34D39922' : 'transparent',
                  color: learningMode === m.key ? '#34D399' : 'rgba(255,255,255,0.55)',
                  border: `1px solid ${learningMode === m.key ? '#34D39944' : 'transparent'}`,
                }}>
                {m.label}
              </button>
            ))}
          </div>
          {isMobile && (
            <button onClick={() => setShowMobileInfo(true)}
              className="text-[11px] px-2.5 py-1 rounded-md"
              style={{ background: 'rgba(52,211,153,0.18)', color: '#5EEAD4', border: '1px solid rgba(52,211,153,0.45)' }}>
              問題/解説
            </button>
          )}
        </div>
      </header>

      {/* 採点後バー */}
      {score && (
        <div className="px-3 md:px-6 py-2 border-b border-white/10 flex items-center justify-between gap-2"
          style={{ background: 'rgba(10,14,26,0.88)' }}>
          <div className="flex items-center gap-2">
            <span className="text-xs px-2.5 py-1 rounded-full font-bold"
              style={{
                background: score.isCorrect ? 'rgba(34,197,94,0.22)' : 'rgba(239,68,68,0.22)',
                color: score.isCorrect ? '#22c55e' : '#ef4444',
              }}>
              {score.isCorrect ? '正解' : '不正解'}
            </span>
            <span className="text-xs text-white/65">スコア {score.score} / 100</span>
          </div>
          <div className="flex gap-2">
            <button onClick={showingAnswer ? resetCanvas : showAnswer}
              className="text-xs px-3 py-1.5 rounded-lg font-semibold text-white"
              style={{ background: 'rgba(52,211,153,0.2)', border: '1px solid rgba(52,211,153,0.45)' }}>
              {showingAnswer ? 'キャンバスを消去' : '正解の構造を見る'}
            </button>
            <button onClick={onNext} disabled={currentIndex >= totalQuestions - 1}
              className="text-xs px-3 py-1.5 rounded-lg font-semibold disabled:opacity-40 text-white"
              style={{ background: 'rgba(20,184,166,0.2)', border: '1px solid rgba(20,184,166,0.45)' }}>
              {currentIndex >= totalQuestions - 1 ? '最後の問題です' : '次の問題へ'}
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden flex-col md:flex-row"
        style={isMobile ? { paddingBottom: '15rem' } : {}}>
        {/* Left: 問題文 + 操作 */}
        <div className={`${isMobile ? 'w-full max-h-[30vh]' : 'w-full md:w-80'} flex flex-col border-b md:border-b-0 md:border-r border-white/10 shrink-0`}
          style={{ background: 'rgba(15,20,35,0.6)' }}>
          <div className="flex-1 min-h-0 overflow-y-auto">
            <div className="p-3 md:p-5 border-b border-white/10">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center text-sm font-bold"
                  style={{ background: '#34D39933', color: '#34D399' }}>
                  Q{question.id}
                </div>
                <h2 className="text-sm md:text-base font-bold text-white">{question.title}</h2>
              </div>
              <p className="text-xs md:text-sm text-white/70 leading-relaxed whitespace-pre-line">
                {question.description}
              </p>
            </div>
            <div className="p-3 md:p-5 text-[11px] md:text-xs text-white/35 leading-relaxed">
              使い方：下のパレットから原子を{isMobile ? 'タップして' : 'ドラッグして'}配置し、
              原子をクリック→別の原子をクリックで結合します。結合の種類は「結合モード」で切り替えられます。
            </div>
          </div>
          {/* アクション */}
          <div className="p-3 md:p-4 border-t border-white/10 flex gap-2 shrink-0">
            <button onClick={checkAnswer} disabled={nodes.length === 0 || showingAnswer}
              className="flex-1 py-2.5 rounded-lg font-bold text-sm transition-all disabled:opacity-30 text-white"
              style={{ background: nodes.length > 0 && !showingAnswer ? 'linear-gradient(135deg, #34D399, #14B8A6)' : '#333' }}>
              採点する
            </button>
            <button onClick={resetCanvas}
              className="px-4 py-2.5 rounded-lg font-bold text-sm text-white/60 hover:text-white"
              style={{ background: 'rgba(255,255,255,0.05)' }}>
              リセット
            </button>
          </div>
        </div>

        {/* Right: キャンバス + 結果 */}
        <div className="flex-1 overflow-hidden flex min-h-0 flex-col md:flex-row">
          <div className="flex-1 relative min-w-0 h-full" ref={reactFlowWrapper}>
            <ReactFlow
              nodes={nodesWithRemove}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onNodeClick={onNodeClick}
              onPaneClick={onPaneClick}
              onInit={setReactFlowInstance}
              onDrop={onDrop}
              onDragOver={onDragOver}
              nodeTypes={nodeTypes}
              fitView={false}
              proOptions={{ hideAttribution: true }}
              minZoom={0.2}
              maxZoom={3}
              deleteKeyCode={['Backspace', 'Delete']}
              style={{ background: 'transparent' }}
            >
              <Background variant="dots" gap={40} size={1} color="rgba(255,255,255,0.05)" />
            </ReactFlow>

            {nodes.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="text-center">
                  <div className="text-6xl mb-4 opacity-20">⚛️</div>
                  <p className="text-white/30 text-lg font-semibold">下のパレットから原子を配置</p>
                  <p className="text-white/20 text-sm mt-1">
                    原子をクリック→別の原子をクリックで結合できます
                  </p>
                </div>
              </div>
            )}

            {pendingSourceNodeId && !showingAnswer && (
              <div className="absolute top-3 left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-full text-xs font-semibold pointer-events-none"
                style={{ background: 'rgba(52,211,153,0.2)', color: '#5EEAD4', border: '1px solid rgba(52,211,153,0.5)' }}>
                結合先の原子をクリックしてください（{bondLabel(bondOrder) || '単結合'}）
              </div>
            )}
          </div>

          {showResult && !isMobile && (
            <div className="w-full md:w-[420px] shrink-0 border-t md:border-t-0 md:border-l border-white/10 overflow-y-auto"
              style={{ background: 'rgba(10,14,26,0.9)' }}>
              <ScoreOverlay score={score} question={question}
                onRetry={resetCanvas} onClose={() => setShowResult(false)} asPanel />
            </div>
          )}
        </div>
      </div>

      {/* パレット（下部固定） */}
      {!(score && isMobile) && (
        <div className="md:h-56 md:shrink-0 border-t border-white/10"
          style={{
            background: 'rgba(10,14,26,0.95)',
            ...(isMobile ? { position: 'fixed', bottom: 0, left: 0, right: 0, height: '15rem', zIndex: 30 } : {}),
          }}>
          <AtomPalette
            onSelectAtom={handleSelectAtom}
            allowedElements={allowedElements}
            bondOrder={bondOrder}
            onBondOrderChange={setBondOrder}
            enableTapAdd={isMobile}
          />
        </div>
      )}

      {/* モバイル採点後モーダル */}
      {showResult && isMobile && (
        <div className="fixed inset-0 z-50 overflow-y-auto" style={{ background: 'rgba(10,14,26,0.97)' }}>
          <ScoreOverlay score={score} question={question}
            onRetry={() => { resetCanvas(); setShowResult(false) }}
            onClose={() => setShowResult(false)} asPanel />
        </div>
      )}

      {/* モバイル問題/解説 */}
      {isMobile && showMobileInfo && (
        <div className="fixed inset-0 z-50 flex items-end" style={{ background: 'rgba(0,0,0,0.55)' }}
          onClick={() => setShowMobileInfo(false)}>
          <div className="w-full max-h-[78vh] overflow-y-auto rounded-t-2xl p-4"
            style={{ background: 'linear-gradient(145deg, #1a1f35, #111827)', borderTop: '1px solid rgba(255,255,255,0.15)' }}
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-white">問題 / 解説</h3>
              <button onClick={() => setShowMobileInfo(false)}
                className="text-xs px-2.5 py-1 rounded-md text-white/70" style={{ background: 'rgba(255,255,255,0.08)' }}>
                閉じる
              </button>
            </div>
            <div className="p-3 rounded-lg mb-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <p className="text-xs text-white/45 mb-1">問題文</p>
              <p className="text-sm text-white/80 leading-relaxed whitespace-pre-line">{question.description}</p>
            </div>
            <div className="p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <p className="text-xs text-white/45 mb-1">解説</p>
              {score ? (
                <p className="text-sm text-white/75 leading-relaxed whitespace-pre-line">{question.explanation}</p>
              ) : (
                <p className="text-sm text-white/50">解説は採点後に表示されます。</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
